/**
 * FatiguePanel — Condition 22 driver fatigue / hours dashboard (admin-only).
 *
 * Phase 1: visibility. Shows per-driver ONLINE hours (on shift) and DRIVING
 * hours (on jobs), today + this week (Europe/London), from
 * GET /api/admin/fatigue/dashboard. Reference thresholds drive amber/red
 * highlighting — they're guidance only (Phase 2 makes them configurable,
 * Phase 3 optionally enforces). Read-only; touches no protected dispatch areas.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Gauge, RefreshCw, Clock, Car, AlertTriangle, CircleDot,
  Pencil, Save, X, ShieldAlert,
} from "lucide-react";

const POLL_MS = 30000;
const hrs = (n) => `${Number(n || 0).toFixed(1)}h`;

const LIMIT_FIELDS = [
  { key: "driving_day_warn",  label: "Driving · day · warn" },
  { key: "driving_day_crit",  label: "Driving · day · over" },
  { key: "driving_week_warn", label: "Driving · week · warn" },
  { key: "driving_week_crit", label: "Driving · week · over" },
  { key: "online_day_warn",   label: "Online · day · warn" },
  { key: "online_day_crit",   label: "Online · day · over" },
];

const STATUS_MAP = {
  over:        ["OVER", "#991B1B", "#FEE2E2"],
  approaching: ["APPROACHING", "#92400E", "#FEF3C7"],
  ok:          ["OK", "#065F46", "#D1FAE5"],
};

const StatusPill = ({ status }) => {
  const [label, fg, bg] = STATUS_MAP[status] || STATUS_MAP.ok;
  return (
    <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm" style={{ color: fg, background: bg }}
      data-testid={`fatigue-status-${status}`}>
      {label}
    </span>
  );
};

// amber/red colour for an hours value vs warn/crit reference thresholds
const tone = (v, warn, crit) => {
  const x = Number(v || 0);
  if (x >= crit) return "#EF4444";
  if (x >= warn) return "#F59E0B";
  return "#0A0A0A";
};

const HoursCell = ({ value, warn, crit, testid }) => {
  const c = tone(value, warn, crit);
  const flag = Number(value || 0) >= warn;
  return (
    <td className="px-3 py-2 text-right font-mono-tab font-bold" style={{ color: c }} data-testid={testid}>
      <span className="inline-flex items-center gap-1 justify-end">
        {flag && <AlertTriangle className="w-3 h-3" />} {hrs(value)}
      </span>
    </td>
  );
};

const SummaryCard = ({ label, value, accent = "#0A0A0A", icon, testid }) => (
  <div className="bg-white border border-border p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="label-mini text-muted-foreground">{label}</div>
      {icon && <span style={{ color: accent }}>{icon}</span>}
    </div>
    <div className="font-heading text-3xl font-black mt-1" style={{ color: accent }} data-testid={`${testid}-value`}>
      {value}
    </div>
  </div>
);

export function FatiguePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.get("/admin/fatigue/dashboard");
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr("Couldn't load fatigue data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const startEdit = () => {
    const ref = data?.reference || {};
    setForm(LIMIT_FIELDS.reduce((a, f) => ({ ...a, [f.key]: ref[f.key] ?? "" }), {}));
    setEditing(true);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const f of LIMIT_FIELDS) payload[f.key] = Number(form[f.key]);
      await api.put("/admin/fatigue/config", payload);
      toast.success("Fatigue limits updated");
      setEditing(false);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save limits. Check warn < over for each metric.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, POLL_MS);
    return () => clearInterval(t);
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="p-8 text-center text-muted-foreground" data-testid="fatigue-loading">
        <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading fatigue data…
      </div>
    );
  }
  if (err && !data) {
    return <div className="p-8 text-center text-[#991B1B]" data-testid="fatigue-error">{err}</div>;
  }

  const ref = data.reference, fleet = data.fleet, drivers = data.drivers || [];

  return (
    <div className="space-y-6" data-testid="fatigue-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Gauge className="w-6 h-6 text-[#002FA7]" />
          <div>
            <h2 className="font-heading text-2xl font-black tracking-tight flex items-center gap-2">
              Driver Fatigue · Condition 22
              {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" data-testid="fatigue-refreshing" />}
            </h2>
            <div className="text-xs text-muted-foreground">
              Online &amp; driving hours · {data.timezone} · this week (from Monday) · updates every 30s
            </div>
          </div>
        </div>
        <button
          onClick={editing ? () => setEditing(false) : startEdit}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-border bg-white hover:bg-secondary transition-colors"
          data-testid="fatigue-edit-limits-btn"
        >
          {editing ? <><X className="w-3 h-3" /> Cancel</> : <><Pencil className="w-3 h-3" /> Edit limits</>}
        </button>
      </div>

      {/* Over / approaching alert banner (warning-only) */}
      {(fleet.over > 0 || fleet.approaching > 0) && (
        <div
          className={`flex items-start gap-2 border px-4 py-3 ${fleet.over > 0 ? "border-[#FCA5A5] bg-[#FEF2F2]" : "border-[#FDE68A] bg-[#FFFBEB]"}`}
          data-testid="fatigue-alert-banner"
        >
          <ShieldAlert className={`w-5 h-5 mt-0.5 ${fleet.over > 0 ? "text-[#991B1B]" : "text-[#92400E]"}`} />
          <div className="text-sm">
            <span className="font-bold">
              {fleet.over > 0 && `${fleet.over} driver${fleet.over > 1 ? "s" : ""} over limit`}
              {fleet.over > 0 && fleet.approaching > 0 && " · "}
              {fleet.approaching > 0 && `${fleet.approaching} approaching`}
            </span>
            <span className="text-muted-foreground"> — review hours below. Warnings only; drivers are not blocked.</span>
          </div>
        </div>
      )}

      {/* Editable council limits */}
      {editing && (
        <div className="bg-white border border-border p-4" data-testid="fatigue-limits-editor">
          <h3 className="font-heading text-lg font-black tracking-tight mb-1">Condition 22 limits (hours)</h3>
          <p className="text-xs text-muted-foreground mb-3">Set your council's limits. "Warn" must be below "over" for each metric. Warning-only — no hard blocking.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {LIMIT_FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className="label-mini text-muted-foreground">{f.label}</span>
                <input
                  type="number" min="0" max="168" step="0.5"
                  value={form[f.key]}
                  onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  className="mt-1 w-full border border-border px-3 py-2 text-sm font-mono-tab focus:outline-none focus:border-[#0A0A0A]"
                  data-testid={`fatigue-input-${f.key}`}
                />
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={saveConfig} disabled={saving}
              className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] disabled:opacity-60 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
              data-testid="fatigue-save-limits-btn"
            >
              <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save limits"}
            </button>
          </div>
        </div>
      )}

      {/* Fleet summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="fatigue-fleet">
        <SummaryCard label="DRIVERS ONLINE" value={fleet.drivers_online} accent="#10B981"
          icon={<CircleDot className="w-4 h-4" />} testid="fatigue-online-count" />
        <SummaryCard label="DRIVING HRS TODAY" value={hrs(fleet.driving_hours_today)} accent="#002FA7"
          icon={<Car className="w-4 h-4" />} testid="fatigue-driving-today" />
        <SummaryCard label="DRIVING HRS THIS WEEK" value={hrs(fleet.driving_hours_week)} accent="#0A0A0A"
          icon={<Car className="w-4 h-4" />} testid="fatigue-driving-week" />
        <SummaryCard label="DRIVERS OVER DAY LIMIT" value={fleet.at_or_over_day_warn}
          accent={fleet.at_or_over_day_warn > 0 ? "#EF4444" : "#0A0A0A"}
          icon={<AlertTriangle className="w-4 h-4" />} testid="fatigue-over-limit" />
      </div>

      {/* Per-driver table */}
      <div className="bg-white border border-border overflow-x-auto" data-testid="fatigue-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary">
              <th className="px-3 py-2 text-left label-mini text-muted-foreground">Driver</th>
              <th className="px-3 py-2 text-right label-mini text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />Online today</span>
              </th>
              <th className="px-3 py-2 text-right label-mini text-muted-foreground">Online week</th>
              <th className="px-3 py-2 text-right label-mini text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Car className="w-3 h-3" />Driving today</span>
              </th>
              <th className="px-3 py-2 text-right label-mini text-muted-foreground">Driving week</th>
              <th className="px-3 py-2 text-right label-mini text-muted-foreground">Jobs (wk)</th>
              <th className="px-3 py-2 text-center label-mini text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {drivers.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground italic" data-testid="fatigue-empty">No drivers found.</td></tr>
            ) : drivers.map((d) => (
              <tr key={d.driver_id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid="fatigue-row">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${d.is_online ? "bg-[#10B981]" : "bg-muted-foreground/40"}`} />
                    <span className="font-bold" data-testid="fatigue-row-name">{d.name}</span>
                  </div>
                </td>
                <HoursCell value={d.online_hours_today} warn={ref.online_day_warn} crit={ref.online_day_crit} testid="fatigue-online-today" />
                <td className="px-3 py-2 text-right font-mono-tab text-muted-foreground">{hrs(d.online_hours_week)}</td>
                <HoursCell value={d.driving_hours_today} warn={ref.driving_day_warn} crit={ref.driving_day_crit} testid="fatigue-driving-today-cell" />
                <HoursCell value={d.driving_hours_week} warn={ref.driving_week_warn} crit={ref.driving_week_crit} testid="fatigue-driving-week-cell" />
                <td className="px-3 py-2 text-right font-mono-tab text-muted-foreground">{d.rides_week}</td>
                <td className="px-3 py-2 text-center"><StatusPill status={d.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-4" data-testid="fatigue-legend">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ background: "#F59E0B" }} /> Approaching limit (driving ≥ {ref.driving_day_warn}h/day · {ref.driving_week_warn}h/week)</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ background: "#EF4444" }} /> Over limit (driving ≥ {ref.driving_day_crit}h/day · {ref.driving_week_crit}h/week)</span>
        <span className="italic">Reference thresholds — configurable in a later phase.</span>
      </div>
    </div>
  );
}

export default FatiguePanel;
